import { useState } from 'react';

interface CodeBlockProps {
  code: string;
  language?: string;
}

export function CodeBlock({ code, language = 'bash' }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative bg-slate-950/50 rounded-xl border border-slate-800 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900/50 border-b border-slate-800">
        <span className="text-xs text-slate-400 font-mono">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-2 py-1 text-xs text-slate-400 hover:text-white transition-colors whitespace-nowrap"
        >
          <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <pre className="p-5 overflow-x-auto">
        <code className="text-sm text-slate-300">{code}</code>
      </pre>
    </div>
  );
}

interface InfoBoxProps {
  type: 'info' | 'warning' | 'success' | 'error';
  title?: string;
  children: React.ReactNode;
}

export function InfoBox({ type, title, children }: InfoBoxProps) {
  const styles = {
    info: {
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      icon: 'ri-information-line',
      iconColor: 'text-blue-400',
      titleColor: 'text-blue-300'
    },
    warning: {
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/30',
      icon: 'ri-alert-line',
      iconColor: 'text-yellow-400',
      titleColor: 'text-yellow-300'
    },
    success: {
      bg: 'bg-green-500/10',
      border: 'border-green-500/30',
      icon: 'ri-checkbox-circle-line',
      iconColor: 'text-green-400',
      titleColor: 'text-green-300'
    },
    error: {
      bg: 'bg-red-500/10',
      border: 'border-red-500/30',
      icon: 'ri-error-warning-line',
      iconColor: 'text-red-400',
      titleColor: 'text-red-300'
    }
  };

  const style = styles[type];

  return (
    <div className={`${style.bg} border ${style.border} rounded-xl p-5`}>
      <div className="flex gap-3">
        <i className={`${style.icon} ${style.iconColor} text-xl flex-shrink-0 mt-0.5`}></i>
        <div className="flex-1 min-w-0">
          {title && (
            <div className={`font-semibold ${style.titleColor} mb-1`}>{title}</div>
          )}
          <div className="text-slate-300 text-base leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

interface SectionTitleProps {
  children: React.ReactNode;
  id?: string;
}

export function SectionTitle({ children, id }: SectionTitleProps) {
  return (
    <h2 id={id} className="text-3xl font-bold text-white mb-4 scroll-mt-24">
      {children}
    </h2>
  );
}

export function SubsectionTitle({ children, id }: SectionTitleProps) {
  return (
    <h3 id={id} className="text-2xl font-semibold text-white mb-3 scroll-mt-24">
      {children}
    </h3>
  );
}

interface TableProps {
  headers: string[];
  rows: (string | React.ReactNode)[][];
}

export function Table({ headers, rows }: TableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-slate-900/50 border-b border-slate-800">
            {headers.map((header, idx) => (
              <th
                key={idx}
                className="px-4 py-3 text-left text-sm font-semibold text-slate-300"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr
              key={rowIdx}
              className="border-b border-slate-800/50 hover:bg-slate-900/30 transition-colors"
            >
              {row.map((cell, cellIdx) => (
                <td key={cellIdx} className="px-4 py-3 text-sm text-slate-300">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error';
}

export function Badge({ children, variant = 'default' }: BadgeProps) {
  const styles = {
    default: 'bg-slate-700/50 text-slate-300',
    success: 'bg-green-500/20 text-green-400',
    warning: 'bg-yellow-500/20 text-yellow-400',
    error: 'bg-red-500/20 text-red-400'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[variant]}`}>
      {children}
    </span>
  );
}